/**
 * The forgotten payee suggestions (TMMAPP-0138 item 6; TMMAPI-0174 §G):
 * `GET /v1/me/payees/suggestions/forgotten`, newest decision first, Load more on `nextCursor`. Each
 * row is the readable name, the suggested name, its transactions, when it was forgotten and, when
 * served, the sample bank line (mono). Bring back restores it (`…/restore`): the row leaves the
 * dialog and the list refetches. 600 wide, full-screen below `sm`. The first load and Load more read
 * on past empty pages that carry a cursor (TMMAPP-0139 item 9); "nothing forgotten" shows only when
 * no item came back and no cursor remains.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import type { ForgottenPayeeSuggestion } from '@/api/payeeSuggestions';
import { ErrorState } from '@/components/ErrorState';
import { SubmitButton } from '@/components/SubmitButton';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';
import { readForgotten } from './forgottenPages';

const PAGE_SIZE = 50;

interface ForgottenSuggestionsDialogProps {
  /** Restores one suggestion; resolves true when it left the forgotten list. */
  onRestore: (id: number) => Promise<boolean>;
  onClose: () => void;
}

export function ForgottenSuggestionsDialog({
  onRestore,
  onClose,
}: ForgottenSuggestionsDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const timeZone = me.user.timezone ?? 'UTC';
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const [items, setItems] = useState<ForgottenPayeeSuggestion[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [restoring, setRestoring] = useState<number | null>(null);

  const loadFirst = useCallback(async () => {
    setFailure(null);
    try {
      const page = await readForgotten(null, PAGE_SIZE);
      setItems(page.items);
      setCursor(page.nextCursor);
    } catch (error) {
      setFailure({ error });
    }
  }, []);

  // StrictMode double-invokes the effect; the first page is read once.
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void loadFirst();
  }, [loadFirst]);

  async function loadMore() {
    if (cursor === null || loadingMore || items === null) return;
    setLoadingMore(true);
    setFailure(null);
    try {
      const page = await readForgotten(cursor, PAGE_SIZE);
      const seen = new Set(items.map((item) => item.id));
      setItems([...items, ...page.items.filter((item) => !seen.has(item.id))]);
      setCursor(page.nextCursor);
    } catch (error) {
      setFailure({ error });
    } finally {
      setLoadingMore(false);
    }
  }

  async function bringBack(id: number) {
    if (restoring !== null) return;
    setRestoring(id);
    try {
      if (await onRestore(id)) {
        setItems((prev) => (prev === null ? prev : prev.filter((item) => item.id !== id)));
      }
    } finally {
      setRestoring(null);
    }
  }

  function decided(at: string | null): string | null {
    if (at === null) return null;
    try {
      return formatInstant(at, locale, timeZone, { dateStyle: 'medium' });
    } catch {
      return null;
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      aria-labelledby={titleId}
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 600 } }}
      data-forgotten-suggestions=""
    >
      <DialogTitle id={titleId}>{t('naming.forgottenTitle')}</DialogTitle>
      <DialogContent>
        {failure !== null && (
          <ErrorState
            variant="section"
            error={failure.error}
            what={t('errors.what.forgottenSuggestions')}
            onRetry={() => void (items === null ? loadFirst() : loadMore())}
          />
        )}
        {items === null && failure === null && (
          <Skeleton variant="rounded" height={120} aria-busy="true" />
        )}
        {items !== null && items.length === 0 && cursor === null && (
          <Typography variant="body2" sx={{ color: tokens.text2 }}>
            {t('naming.forgottenEmpty')}
          </Typography>
        )}
        {items !== null && items.length > 0 && (
          <Box
            component="ul"
            aria-label={t('naming.forgottenTitle')}
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {items.map((item) => {
              const when = decided(item.decidedAt);
              return (
                <Box
                  component="li"
                  key={item.id}
                  data-forgotten-suggestion={item.id}
                  sx={{
                    py: 1.5,
                    borderTop: `1px solid ${tokens.line}`,
                    '&:first-of-type': { borderTop: 0 },
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    sx={{ gap: 1, alignItems: { sm: 'flex-start' } }}
                  >
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}
                      >
                        {item.displayName}
                      </Typography>
                      {item.proposedName.length > 0 && (
                        <Typography variant="body2">
                          <Trans
                            i18nKey="naming.suggestedName"
                            values={{ name: item.proposedName }}
                            components={{ b: <Box component="strong" sx={{ fontWeight: 500 }} /> }}
                          />
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: tokens.text2 }}>
                        {[
                          t('naming.lines', { count: item.lineCount }),
                          when !== null ? t('naming.forgottenOn', { date: when }) : null,
                        ]
                          .filter((part): part is string => part !== null)
                          .join(' · ')}
                      </Typography>
                      {item.sampleText !== null && (
                        <Typography
                          variant="mono"
                          component="span"
                          data-sample-text=""
                          sx={{ color: tokens.text2, overflowWrap: 'anywhere' }}
                        >
                          {item.sampleText}
                        </Typography>
                      )}
                    </Stack>
                    <SubmitButton
                      size="small"
                      variant="outlined"
                      loading={restoring === item.id}
                      disabled={restoring !== null && restoring !== item.id}
                      aria-label={t('naming.bringBackNamed', { name: item.displayName })}
                      onClick={() => void bringBack(item.id)}
                      sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    >
                      {t('naming.bringBack')}
                    </SubmitButton>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {cursor !== null && items !== null && (
          <SubmitButton variant="outlined" loading={loadingMore} onClick={() => void loadMore()}>
            {t('common.loadMore')}
          </SubmitButton>
        )}
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
